import React, { useEffect, useState, useRef } from "react";
import Sidebar from "../Sidebar";
import { useLocation } from "react-router-dom";
import { FaUsers } from "react-icons/fa";
import { LayoutDashboard } from "lucide-react";
import axios from "axios";
import { getDB, dbGet, dbPut } from "../../db";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import JSZip from "jszip";

import "../sprzedaz/DashboardGrid.css";
import TopContractorsZakupyTable from "./TopContractorsZakupyTable";
import ZakupyParetoChart from "./ZakupyParetoChart";
import ZakupyCompanyAnalysis from "./ZakupyCompanyAnalysis";
import Top10PurchaseShare from "./Top10PurchaseShare";
import TopSuppliersByTransactionsPie from "./TopSuppliersByTransactionsPie";
import HistogramPurchaseChart from "./HistogramPurchaseChart";
import PurchaseChangesOverTime from "./PurchaseChangesOverTime";
import MonthlyPurchaseByContractorChart from "./MonthlyPurchaseByContractorChart";
import WeeklyPurchaseByContractorChart from "./WeeklyPurchaseByContractorChart";
import TopPurchaseTransactions from "./TopPurchaseTransactions";
import NewVsRegularSuppliers from "./NewVsRegularSuppliers";
import ZakupyByContractorTable from "./ZakupyByContractorTable";
import DailyUniqueSuppliersChart from "./DailyUniqueSuppliersChart";
import TopBar from "../TopBar";
import { createGlobalStyle } from "styled-components";

// NEW: shared, license-gated buttons + token decode
import { DownloadButtons } from "../DownloadButtons";
import { jwtDecode } from "jwt-decode";

/* ----------------------- EXPORT MODE (generic) ----------------------- */
const ExportModeStyles = createGlobalStyle`
  .for-export * { pointer-events: none !important; cursor: default !important; }
  .for-export [data-export-hide="true"] { display: none !important; }
`;
const enableExportMode = () => document.body.classList.add("for-export");
const disableExportMode = () => document.body.classList.remove("for-export");
/* -------------------------------------------------------------------- */

const cardStyle = {
  border: "1px solid #ccc",
  borderRadius: "8px",
  padding: "20px 20px 40px 20px",
  backgroundColor: "#f9f9f9",
  boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
  overflow: "hidden",
  textAlign: "center",
};

const SectionTitle = ({ title, inGrid = false }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: "10px",
      padding: "10px 20px 12px 20px",
      margin: inGrid ? "8px 0 12px 0" : "8px 20px 12px 20px",
      fontSize: "1.05rem",
      fontWeight: 700,
      color: "#2b2b2b",
      position: "relative",
      background:
        "linear-gradient(to right, rgba(235, 135, 227, 0.12), rgba(227, 135, 235, 0.04))",
      border: "1px solid rgba(235, 135, 230, 0.35)",
      borderRadius: "8px",
      ...(inGrid ? { gridColumn: "1 / -1", paddingLeft: "20px", paddingRight: "20px" } : {}),
    }}
  >
    <LayoutDashboard size={20} strokeWidth={2} color="#eb87deff" />
    <span style={{ position: "relative", display: "inline-block" }}>
      {title}
      <span
        style={{
          position: "absolute",
          left: 0,
          bottom: -3,
          width: "100%",
          height: 2,
          backgroundColor: "#eb87beff",
          borderRadius: 2,
          opacity: 0.7,
        }}
      />
    </span>
  </div>
);

const formatPath = (path) => {
  const customNames = {
    "zakupy/kontrahent": {
      name: "Zakupy / Zakupy wg kontrahenta",
      icon: <FaUsers />,
    },
  };
  const cleanPath = path.replace(/^\//, "");
  return customNames[cleanPath] || { name: cleanPath.replace("/", " / "), icon: null };
};

// Helper: extract license from JWT; treat NO EXPIRE via flag or far-future date
function extractLicenseFromToken(token) {
  if (!token) return null;
  try {
    const d = jwtDecode(token) || {};
    const typeRaw = d.LicenseType || d.licenseType || d.plan || d.Plan || "";
    const expRaw  = d.LicenseExpire ?? d.licenseExpire ?? "";

    const t = String(typeRaw).toUpperCase();
    const e = String(expRaw).toUpperCase();

    const typeHasNoExp = t.includes("NO EXPIRE") || t.includes("NO_EXPIRE");
    const expIsFlag    = e === "NO EXPIRE" || e === "NO_EXPIRE";

    let yearIsFar = false;
    if (!expIsFlag && typeof expRaw === "string" && expRaw) {
      const dt = new Date(expRaw);
      yearIsFar = !isNaN(dt) && dt.getUTCFullYear() >= 2099;
    }

    if (typeHasNoExp || expIsFlag || yearIsFar) return "NO EXPIRE";
    return typeRaw || null;
  } catch {
    return null;
  }
}

const ZakupyKontrahent = () => {
  const [email, setEmail] = useState("");
  const [data, setData] = useState([]);
  const [isDownloading, setIsDownloading] = useState(false);
  const locationData = useLocation();
  const { name, icon } = formatPath(locationData.pathname);

  // NEW: license
  const [licenseType, setLicenseType] = useState(null);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user") || "null");
    if (user?.Email) setEmail(user.Email);

    // license from user object
    const ltUser =
      user?.licenseType ?? user?.LicenseType ?? user?.plan ?? user?.Plan ?? null;
    if (typeof ltUser === "string" && ltUser) setLicenseType(ltUser);

    // license from token
    const token = localStorage.getItem("token");
    const ltFromToken = extractLicenseFromToken(token);
    if (ltFromToken) setLicenseType(ltFromToken);
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      await getDB();
      const cached = await dbGet("zakupy", "allZakupy");
      if (cached?.data?.length) {
        setData(cached.data);
        return;
      }
      const token = localStorage.getItem("token");
      const res = await axios.get("https://server229256.nazwa.pl/api/zakupy", {
        headers: { Authorization: token },
      });
      const purchases = res.data || [];
      setData(purchases);
      await dbPut("zakupy", "allZakupy", {
        data: purchases,
        timestamp: Date.now(),
      });
    } catch (e) {
      console.error("fetchData error:", e);
      setData([]);
    }
  };

  // Refs
  const refs = {
    topContractorsRef: useRef(null),
    top10ShareRef: useRef(null),
    histogramRef: useRef(null),
    topByTransactionsRef: useRef(null),

    paretoChartRef: useRef(null),
    salesChangesRef: useRef(null),

    companyAnalysisRef: useRef(null),
    newVsRegularRef: useRef(null),
    dailyUniqueRef: useRef(null),

    monthlySalesRef: useRef(null),
    weeklySalesRef: useRef(null),

    topTransactionsRef: useRef(null),
    salesByContractorRef: useRef(null),
  };

  /* ------------ helpers for clean exports (hide in-card controls) ------------ */
  const wait = (ms) => new Promise((r) => setTimeout(r, ms));

  const hideCardControlsTemporarily = (rootEl) => {
    const controls = Array.from(
      rootEl.querySelectorAll(
        'input, select, button, [data-export-hide="true"], .filters, .control-row, .toolbar'
      )
    );
    controls.forEach((el) => {
      el.__prevDisplay = el.style.display;
      el.style.display = "none";
    });
    return () => {
      controls.forEach((el) => {
        el.style.display = el.__prevDisplay || "";
        delete el.__prevDisplay;
      });
    };
  };

  const captureElementAsJpeg = async (el) => {
    el.scrollIntoView({ block: "center" });
    const restore = hideCardControlsTemporarily(el);
    await wait(200);
    const canvas = await html2canvas(el, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      logging: false,
      ignoreElements: (node) => {
        if (!(node instanceof Element)) return false;
        return node.matches('[data-export-hide="true"]');
      },
    });
    restore();
    const dataUrl = canvas.toDataURL("image/jpeg", 0.9);
    return { dataUrl, width: canvas.width, height: canvas.height };
  };
  /* -------------------------------------------------------------------------- */

  // === REPORT LAYOUTS ===
  const COMPONENTS_FOR_REPORT = [
    { ref: refs.topContractorsRef,   title: "Top Contractors — Zakupy (Tabela)", layout: "landscape-2col" },
    { ref: refs.top10ShareRef,       title: "Udział Top 10 w zakupach",         layout: "landscape-2col" },
    { ref: refs.histogramRef,        title: "Histogram wartości zakupów",        layout: "landscape-2col" },
    { ref: refs.topByTransactionsRef, title: "Top dostawcy wg liczby transakcji", layout: "landscape-2col" },
    { ref: refs.paretoChartRef,       title: "Pareto 80/20 — Zakupy",             layout: "landscape" },
    { ref: refs.salesChangesRef,      title: "Zmiany zakupów w czasie",           layout: "landscape" },
    { ref: refs.companyAnalysisRef,   title: "Analiza dostawców (liczba/unikalni)", layout: "landscape-2col" },
    { ref: refs.newVsRegularRef,      title: "Nowi vs. stali dostawcy",           layout: "landscape-2col" },
    { ref: refs.dailyUniqueRef,       title: "Dzienna liczba unikalnych dostawców", layout: "landscape" },
    { ref: refs.monthlySalesRef,      title: "Zakupy wg kontrahenta — miesięcznie", layout: "landscape" },
    { ref: refs.weeklySalesRef,       title: "Zakupy wg kontrahenta — tygodniowo",  layout: "landscape" },
    { ref: refs.topTransactionsRef,   title: "Największe transakcje zakupowe",     layout: "landscape" },
    { ref: refs.salesByContractorRef, title: "Zakupy wg kontrahenta — tabela",     layout: "landscape" },
  ];

  const buildPages = async () => {
    const P_MARGINS = { left: 12, right: 12, top: 22, bottom: 16 };
    const L_MARGINS = { left: 12, right: 12, top: 22, bottom: 16 };
    const pages = [];

    const getPageMetrics = (orient) => {
      const isLandscape = orient === "l" || orient === "l2" || orient === "l3";
      const base = isLandscape
        ? { pageW: 297, pageH: 210, margins: L_MARGINS }
        : { pageW: 210, pageH: 297, margins: P_MARGINS };
      const contentW = base.pageW - base.margins.left - base.margins.right;
      const contentH = base.pageH - base.margins.top - base.margins.bottom;
      return { ...base, contentW, contentH };
    };

    let curY = null;
    let l3colCountOnCurrentPage = 0;
    let l2colCountOnCurrentPage = 0;

    const addBlock = (img, title, layout) => {
      const gutter = 6;

      if (layout === "landscape-3col") {
        if (!pages.length || pages[pages.length - 1].orient !== "l3" || l3colCountOnCurrentPage >= 3) {
          pages.push({ orient: "l3", elements: [] });
          l3colCountOnCurrentPage = 0;
        }
        const { margins, contentW, contentH } = getPageMetrics("l");
        const columns = 3;
        const cellW = (contentW - gutter * (columns - 1)) / columns;
        const cellH = contentH * 0.9;

        const col = l3colCountOnCurrentPage;
        const x = margins.left + col * (cellW + gutter);
        const y = margins.top;

        const ratio = img.height / img.width;
        let w = cellW;
        let h = w * ratio;
        if (h > cellH) { h = cellH; w = h / ratio; }

        pages[pages.length - 1].elements.push({
          x,
          y: y + (cellH - h) / 2,
          w,
          h,
          title,
          dataUrl: img.dataUrl,
        });
        l3colCountOnCurrentPage += 1;
        return;
      }

      if (layout === "landscape-2col") {
        if (!pages.length || pages[pages.length - 1].orient !== "l2" || l2colCountOnCurrentPage >= 2) {
          pages.push({ orient: "l2", elements: [] });
          l2colCountOnCurrentPage = 0;
        }
        const { margins, contentW, contentH } = getPageMetrics("l");
        const columns = 2;
        const cellW = (contentW - gutter * (columns - 1)) / columns;
        const cellH = contentH * 0.9;

        const col = l2colCountOnCurrentPage;
        const x = margins.left + col * (cellW + gutter);
        const y = margins.top;

        const ratio = img.height / img.width;
        let w = cellW;
        let h = w * ratio;
        if (h > cellH) { h = cellH; w = h / ratio; }

        pages[pages.length - 1].elements.push({
          x,
          y: y + (cellH - h) / 2,
          w,
          h,
          title,
          dataUrl: img.dataUrl,
        });
        l2colCountOnCurrentPage += 1;
        return;
      }

      if (layout === "landscape") {
        pages.push({ orient: "l", elements: [] });
        const { margins, contentW, contentH } = getPageMetrics("l");
        const ratio = img.height / img.width;
        const targetW = contentW;
        const targetH = Math.min(targetW * ratio, contentH * 0.9);
        const startY = margins.top + (contentH - targetH) / 2;

        pages[pages.length - 1].elements.push({
          x: margins.left,
          y: startY,
          w: targetW,
          h: targetH,
          title,
          dataUrl: img.dataUrl,
        });
        return;
      }

      // portrait flow
      const { margins, contentW, contentH } = getPageMetrics("p");
      const targetW = contentW;
      const ratio = img.height / img.width;
      const targetH = Math.min(targetW * ratio, contentH * 0.9);

      if (!pages.length || pages[pages.length - 1].orient !== "p") {
        pages.push({ orient: "p", elements: [] });
        const { margins: m } = getPageMetrics("p");
        curY = m.top;
      }
      if (curY + targetH > margins.top + contentH) {
        pages.push({ orient: "p", elements: [] });
        curY = margins.top;
      }

      pages[pages.length - 1].elements.push({
        x: margins.left,
        y: curY,
        w: targetW,
        h: targetH,
        title,
        dataUrl: img.dataUrl,
      });

      curY += targetH + 6;
    };

    // capture & place
    for (const block of COMPONENTS_FOR_REPORT) {
      if (!block.ref?.current) continue;
      const img = await captureElementAsJpeg(block.ref.current);
      addBlock(img, block.title, block.layout);
    }

    return { pages, getPageMetrics };
  };

  const handleDownloadPDF = async () => {
    setIsDownloading(true);
    enableExportMode();
    try {
      const reportTitle = "Zakupy wg kontrahenta — Raport";
      const { pages, getPageMetrics } = await buildPages();

      if (pages.length === 0) pages.push({ orient: "p", elements: [] });
      const firstOrient = pages[0].orient === "p" ? "portrait" : "landscape";
      const pdf = new jsPDF({ orientation: firstOrient, unit: "mm", format: "a4", compress: true });

      const drawHeaderFooter = (pdf, page, pageCount, opts) => {
        const { leftMargin, topMargin, rightMargin, bottomMargin, title } = opts;
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(12);
        pdf.text(title, leftMargin, topMargin - 5, { baseline: "bottom" });
        pdf.setLineWidth(0.2);
        pdf.line(leftMargin, topMargin - 3, pdf.internal.pageSize.getWidth() - rightMargin, topMargin - 3);

        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(9);
        const pageLabel = `Page ${page} / ${pageCount}`;
        const dateLabel = new Date().toISOString().slice(0, 19).replace("T", " ");
        const y = pdf.internal.pageSize.getHeight() - bottomMargin + 5;
        pdf.text(pageLabel, leftMargin, y);
        const w = pdf.getTextWidth(dateLabel);
        pdf.text(dateLabel, pdf.internal.pageSize.getWidth() - rightMargin - w, y);
      };

      const pageCount = pages.length;
      pages.forEach((pg, i) => {
        if (i > 0) {
          const ori = pg.orient === "p" ? "portrait" : "landscape"; // l, l2, l3 -> landscape
          pdf.addPage("a4", ori);
        }
        const { margins } = getPageMetrics(pg.orient);
        drawHeaderFooter(pdf, i + 1, pageCount, {
          leftMargin: margins.left,
          rightMargin: margins.right,
          topMargin: margins.top,
          bottomMargin: margins.bottom,
          title: reportTitle,
        });
        pg.elements.forEach((el) => {
          pdf.setFont("helvetica", "bold");
          pdf.setFontSize(10);
          const titleY = el.y - 2;
          if (titleY > margins.top) pdf.text(el.title, el.x, titleY);
          pdf.addImage(el.dataUrl, "JPEG", el.x, el.y, el.w, el.h, undefined, "FAST");
        });
      });

      pdf.save(`Zakupy_kontrahenci_${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (err) {
      console.error(err);
      alert("Wystąpił błąd podczas generowania PDF.");
    } finally {
      disableExportMode();
      setIsDownloading(false);
    }
  };

  const handleDownloadPNG = async () => {
    setIsDownloading(true);
    enableExportMode();
    try {
      const zip = new JSZip();

      for (const c of COMPONENTS_FOR_REPORT) {
        if (!c.ref?.current) continue;
        c.ref.current.scrollIntoView({ block: "center" });
        const restore = hideCardControlsTemporarily(c.ref.current);
        await wait(200);
        const canvas = await html2canvas(c.ref.current, {
          scale: 2,
          useCORS: true,
          backgroundColor: "#ffffff",
          logging: false,
          ignoreElements: (node) => {
            if (!(node instanceof Element)) return false;
            return node.matches('[data-export-hide="true"]');
          },
        });
        restore();

        const imgData = canvas.toDataURL("image/png");
        zip.file(`${c.title.replace(/\s+/g, "_")}.png`, imgData.split("base64,")[1], { base64: true });
      }

      const blob = await zip.generateAsync({ type: "blob" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `Zakupy_kontrahenci_${new Date().toISOString().slice(0, 10)}.zip`;
      link.click();
    } catch (err) {
      console.error(err);
      alert("Wystąpił błąd podczas generowania PNG.");
    } finally {
      disableExportMode();
      setIsDownloading(false);
    }
  };

  return (
    <>
      <ExportModeStyles />

      <div style={{ display: "flex", height: "100vh" }}>
        <Sidebar />

        <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
          {/* Top bar inside the content column */}
          <TopBar />

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "10px 20px",
              fontSize: "1.2rem",
              fontWeight: "bold",
              color: "#9848c7",
              marginBottom: "8px",
              textTransform: "uppercase",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              {icon} {name}
            </div>

            {/* License-gated export buttons (PDF/PNG) */}
            <DownloadButtons
              licenseType={licenseType}
              onDownloadPDF={handleDownloadPDF}
              onDownloadPNG={handleDownloadPNG}
              disabled={isDownloading}
            />
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              position: "relative",
              marginTop: "2px",
              color: "#444",
              fontSize: "1rem",
              padding: "5px 20px 10px 20px",
              maxWidth: "90%",
              lineHeight: 1.5,
            }}
          />

          <div
            style={{
              flex: 1,
              overflow: "auto",
              padding: "20px",
              backgroundColor: "#f5f5f5",
            }}
          >
            <div className="dashboard-grid" style={{ marginBottom: "20px" }}>
              <SectionTitle
                inGrid
                title="Przegląd i porównanie 10 najlepszych dostawców - ranking oraz porównanie dostawców miesiąc do miesiąca i rok do koku. Zobaczysz także udział top 10 dostawców w zakupach"
              />

              <div className="card-span-6" style={cardStyle} ref={refs.topContractorsRef}>
                <TopContractorsZakupyTable data={data} />
              </div>
              <div className="card-span-6" style={cardStyle} ref={refs.top10ShareRef}>
                <Top10PurchaseShare data={data} />
              </div>

              <div className="card-span-6" style={cardStyle} ref={refs.histogramRef}>
                <HistogramPurchaseChart data={data} />
              </div>
              <div className="card-span-6" style={cardStyle} ref={refs.topByTransactionsRef}>
                <TopSuppliersByTransactionsPie data={data} />
              </div>

              <div className="card-span-12" style={cardStyle} ref={refs.paretoChartRef}>
                <ZakupyParetoChart data={data} />
              </div>
              <div className="card-span-12" style={cardStyle} ref={refs.salesChangesRef}>
                <PurchaseChangesOverTime data={data} />
              </div>
            </div>

            <SectionTitle
              inGrid
              title="Analiza liczby dostawców i ilość dostawców unikalnych w różnych okresach. Porównania ilości nowych i stałych kontrahentów w okresach."
            />

            <div className="dashboard-grid" style={{ marginBottom: "20px" }}>
              <div className="card-span-6" style={cardStyle} ref={refs.companyAnalysisRef}>
                <ZakupyCompanyAnalysis data={data} />
              </div>
              <div className="card-span-6" style={cardStyle} ref={refs.newVsRegularRef}>
                <NewVsRegularSuppliers data={data} />
              </div>

              <div className="card-span-12" style={cardStyle} ref={refs.dailyUniqueRef}>
                <DailyUniqueSuppliersChart data={data} />
              </div>

              <SectionTitle
                inGrid
                title="Prównanie wybranych dostawców ze sobą w okresach miesięcznych i tygodniowych"
              />
            </div>

            <div className="dashboard-grid">
              <div className="card-span-12" style={cardStyle} ref={refs.monthlySalesRef}>
                <MonthlyPurchaseByContractorChart data={data} />
              </div>
              <div className="card-span-12" style={cardStyle} ref={refs.weeklySalesRef}>
                <WeeklyPurchaseByContractorChart data={data} />
              </div>

              <SectionTitle
                inGrid
                title="Przegląd wartości zakupów wszystkich dostawców – ranking, porównianie do poprzedniego miesiąca i roku"
              />

              <div className="card-span-12" style={cardStyle} ref={refs.topTransactionsRef}>
                <TopPurchaseTransactions data={data} />
              </div>

              <div className="card-span-12" style={cardStyle} ref={refs.salesByContractorRef}>
                <ZakupyByContractorTable data={data} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {isDownloading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(255,255,255,0.8)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "1.5rem",
            fontWeight: "bold",
            color: "#333",
          }}
        >
          Poczekaj, trwa generowanie raportu... ⏳
        </div>
      )}
    </>
  );
};

export default ZakupyKontrahent;
